/**
 * ProfilePage - Studio profile management
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getStudioProfile,
  updateStudioProfile,
  type Studio,
  type UpdateStudioData,
  type SavedCard,
  formatAddress,
  getSavedCard,
  createCardSetupSession,
  confirmCardUpdate,
  describeSavedCard,
} from '../../api/studios';
import { DashboardMobileNav } from '../../components/dashboard';
import { PlacesAutocomplete, type PlaceResult } from '../../components/events/PlacesAutocomplete';
import { formatPhoneDisplay } from '../../utils/phone';
import { isValidPostalCode } from '@sda/shared';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  // Canadian provinces/territories — let Canadian studios maintain their address (SDA-197)
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [studio, setStudio] = useState<Studio | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Email verification resend state (SDA-161/162)
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationResendSuccess, setVerificationResendSuccess] = useState(false);
  const [verificationResendError, setVerificationResendError] = useState<string | null>(null);

  const handleResendVerification = async () => {
    if (!user?.email || isResendingVerification) return;
    try {
      setIsResendingVerification(true);
      setVerificationResendError(null);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      if (!response.ok) throw new Error('Failed to resend verification email');
      setVerificationResendSuccess(true);
    } catch (err) {
      setVerificationResendError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setIsResendingVerification(false);
    }
  };

  // Form state
  const [formData, setFormData] = useState<UpdateStudioData>({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    description: '',
    google_place_id: '',
  });

  // Card on file (SDA-292)
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [isCardLoading, setIsCardLoading] = useState(true);
  const [isCardRedirecting, setIsCardRedirecting] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardNotice, setCardNotice] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  // Load the card on file, or finish a card update when Stripe sends the
  // studio back here (?card_updated=true&session_id=… / ?card_cancelled=true).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('card_updated') === 'true' && sessionId) {
      window.history.replaceState({}, '', window.location.pathname);
      confirmCardUpdate(sessionId)
        .then((card) => {
          setSavedCard(card);
          setSuccessMessage('Card updated. Future payments will use your new card.');
        })
        .catch(() => {
          setCardError('We could not confirm your new card. Please refresh the page or try again.');
          loadSavedCard();
        })
        .finally(() => setIsCardLoading(false));
      return;
    }
    if (params.get('card_cancelled') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      setCardNotice('Card update cancelled. Your card on file was not changed.');
    }
    loadSavedCard();
  }, []);

  const loadSavedCard = async () => {
    try {
      setIsCardLoading(true);
      setSavedCard(await getSavedCard());
    } catch {
      setCardError('Could not load your card on file.');
    } finally {
      setIsCardLoading(false);
    }
  };

  const handleUpdateCard = async () => {
    try {
      setIsCardRedirecting(true);
      setCardError(null);
      setCardNotice(null);
      const { checkoutUrl } = await createCardSetupSession('/dashboard/profile');
      window.location.href = checkoutUrl;
    } catch (err) {
      setCardError(err instanceof Error ? err.message : 'Failed to start the card update');
      setIsCardRedirecting(false);
    }
  };

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getStudioProfile();
      setStudio(data);
      setFormData({
        name: data.name,
        address_line1: data.address_line1 || '',
        address_line2: data.address_line2 || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code,
        phone: data.phone || '',
        website: data.website || '',
        description: data.description || '',
        google_place_id: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!isValidPostalCode(formData.zip_code || '')) {
      setError('Please enter a valid US ZIP or Canadian postal code');
      return;
    }

    try {
      setIsSaving(true);
      // Send undefined (not '') when no city was picked — the schema's
      // google_place_id is .min(1).optional() and would reject an empty string.
      const updatedStudio = await updateStudioProfile({
        ...formData,
        google_place_id: formData.google_place_id || undefined,
      });
      setStudio(updatedStudio);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Live-mask the Phone Number input as the user types. Delegates to the
  // shared formatPhoneDisplay: US numbers get (XXX) XXX-XXXX grouping;
  // numbers starting with "+" pass through as international (SDA-112).
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, phone: formatPhoneDisplay(e.target.value) }));
  };

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                Dashboard
              </Link>
              <Link to="/dashboard/bookings" className="text-warm-600 hover:text-warm-900 text-sm">
                My Bookings
              </Link>
              <Link to="/dashboard/inquiries" className="text-warm-600 hover:text-warm-900 text-sm">
                My Inquiries
              </Link>
              <Link to="/dashboard/profile" className="text-primary-600 font-medium text-sm">
                Studio Profile
              </Link>
              <Link to="/dashboard/contacts" className="text-warm-600 hover:text-warm-900 text-sm">
                Contacts
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
              <button
                onClick={() => logout()}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <DashboardMobileNav />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="hidden sm:block mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">
                Dashboard
              </Link>
            </li>
            <li className="text-warm-400">/</li>
            <li className="text-warm-600">Studio Profile</li>
          </ol>
        </nav>

        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
            Studio Profile
          </h1>
          <p className="hidden sm:block text-warm-600">
            Update your studio information. This is used for booking communications and pricing
            calculations.
          </p>
        </div>

        {/* Email Verification Card — only renders when email is not verified (SDA-161/162) */}
        {user && !user.email_verified && (
          <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-warning-800">Verify your email address</p>
                <p className="text-sm text-warning-700 mt-1">
                  Check your inbox for the verification link sent to <strong>{user.email}</strong>.
                  Didn't get it?
                </p>
                {verificationResendSuccess && (
                  <p className="text-sm text-success-700 mt-2">
                    Verification email sent — check your inbox.
                  </p>
                )}
                {verificationResendError && (
                  <p className="text-sm text-danger-700 mt-2">{verificationResendError}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={isResendingVerification || verificationResendSuccess}
                className="text-sm font-medium text-warning-800 hover:text-warning-900 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResendingVerification ? 'Sending...' : verificationResendSuccess ? 'Sent' : 'Resend Email'}
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-success-700">{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
          </div>
        )}

        {isLoading ? (
          <div className="card">
            <div className="animate-pulse space-y-6">
              <div className="h-10 bg-warm-100 rounded w-1/3" />
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 bg-warm-100 rounded" />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Studio Information */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-6">
                Studio Information
              </h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="label">
                    Studio Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className="input w-full"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="label">
                      Phone Number *
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone || ''}
                      onChange={handlePhoneChange}
                      className="input w-full"
                      placeholder="(555) 123-4567 or +44 20 7946 0958"
                      pattern="^(\(\d{3}\) \d{3}-\d{4}|\+[\d ()\-\.]{6,24})$"
                      title="US format, or start with + for international"
                      maxLength={25}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="website" className="label">
                      Website
                    </label>
                    <input
                      id="website"
                      name="website"
                      type="url"
                      value={formData.website || ''}
                      onChange={handleChange}
                      className="input w-full"
                      placeholder="https://www.yourstudio.com"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-6">
                Address
              </h2>
              <p className="text-sm text-warm-500 mb-4">
                Your address is used to calculate travel distances for pricing.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="address_line1" className="label">
                    Street Address *
                  </label>
                  <input
                    id="address_line1"
                    name="address_line1"
                    type="text"
                    value={formData.address_line1}
                    onChange={handleChange}
                    className="input w-full"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="address_line2" className="label">
                    Address Line 2
                  </label>
                  <input
                    id="address_line2"
                    name="address_line2"
                    type="text"
                    value={formData.address_line2 || ''}
                    onChange={handleChange}
                    className="input w-full"
                    placeholder="Suite, Unit, Building, etc."
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="city" className="label">
                      City *
                    </label>
                    <PlacesAutocomplete
                      mode="city"
                      id="city"
                      value={formData.city || ''}
                      onInputChange={(t) => setFormData((f) => ({ ...f, city: t, state: '', google_place_id: '' }))}
                      onPlaceSelected={(p: PlaceResult) => setFormData((f) => ({
                        ...f,
                        city: p.addressComponents?.city || p.name || f.city,
                        state: p.addressComponents?.state || f.state,
                        google_place_id: p.placeId || '',
                      }))}
                      placeholder="Start typing a city…"
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="label">
                      State *
                    </label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="input w-full"
                      required
                    >
                      <option value="">Select state</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="zip_code" className="label">
                      ZIP / Postal Code *
                    </label>
                    <input
                      id="zip_code"
                      name="zip_code"
                      type="text"
                      value={formData.zip_code}
                      onChange={handleChange}
                      className="input w-full"
                      placeholder="10001 or K1C 6E7"
                      autoComplete="postal-code"
                      maxLength={7}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-6">
                Additional Notes
              </h2>
              <p className="text-sm text-warm-500 mb-4">
                Any additional information you'd like us to know about your studio.
              </p>

              <div>
                <label htmlFor="description" className="label">
                  Notes
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  className="input w-full"
                  rows={4}
                  maxLength={1000}
                  placeholder="Special requirements, parking info, class schedules, etc."
                />
                <p className="mt-1 text-sm text-warm-400">
                  {(formData.description || '').length}/1000 characters
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4">
              <Link
                to="/dashboard"
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                &larr; Back to Dashboard
              </Link>

              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary px-8 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Payment method (SDA-292) — outside the form so its button can't submit it */}
        <div className="card mt-6" data-testid="payment-method-card">
          <h2 className="text-lg font-semibold text-warm-900 mb-2">Payment method</h2>
          {isCardLoading ? (
            <div className="animate-pulse h-6 bg-warm-100 rounded w-1/2" />
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-warm-700" data-testid="saved-card-summary">
                {savedCard ? describeSavedCard(savedCard) : 'No card on file'}
              </p>
              <button
                type="button"
                onClick={handleUpdateCard}
                disabled={isCardRedirecting}
                className="btn-secondary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCardRedirecting ? 'Redirecting...' : savedCard ? 'Update card' : 'Add card'}
              </button>
            </div>
          )}
          <p className="mt-3 text-sm text-warm-500">
            Updating your card doesn't charge you. Future payments use the new card and your old card is removed.
          </p>
          {cardNotice && <p className="mt-3 text-sm text-warm-700">{cardNotice}</p>}
          {cardError && <p className="mt-3 text-sm text-danger-700">{cardError}</p>}
        </div>

        {/* Current Address Display */}
        {studio && (
          <div className="mt-8 p-4 bg-warm-100 rounded-lg">
            <p className="text-sm text-warm-500 mb-1">Current registered address:</p>
            <p className="font-medium text-warm-700">{formatAddress(studio)}</p>
          </div>
        )}
      </main>
    </div>
  );
}
